import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, KPIDeleteConfirmModal } from '../../components';
import { useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { organizationalUnitsApi, goalsApi, kpisApi } from '../../services/apiService';
import { StrategicGoalList, OrganizationalUnit, KPIType, Polarity, Frequency } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { normalizeArabicNumbers } from '../../utils/rtl';
import { invalidate as invalidateData } from '../../utils/dataInvalidation';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;
type RouteProps = RouteProp<MainStackParamList, 'AddOrganizationalUnit'>;

// ============================================================
// SVG Icons
// ============================================================
const ChevronLeftIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const MenuIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 6H20M4 12H20M4 18H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 2V6M8 2V6M3 10H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// KPI Form Data Interface
interface KPIFormData {
  id: string;
  name: string;
  description: string;
  weight: string;
  kpiType: string;
  polarity: string;
  unit: string;
  targetValue: string;
  worstAcceptableValue: string;
  frequency: string;
  measurementFormula: string;
  dataSource: string;
  measurementStartDate: string;
  isExpanded: boolean;
}

const createEmptyKPI = (): KPIFormData => ({
  id: Math.random().toString(36).substr(2, 9),
  name: '',
  description: '',
  weight: '',
  kpiType: '',
  polarity: '',
  unit: '',
  targetValue: '',
  worstAcceptableValue: '',
  frequency: '',
  measurementFormula: '',
  dataSource: '',
  measurementStartDate: '',
  isExpanded: true,
});

export default function AddOrganizationalUnitScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const unitId = route.params?.unitId;
  const parentId = route.params?.parentId;
  const parentName = route.params?.parentName;
  const { navigateToHome, navigateToMyOrganization, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  const scrollToKpi = route.params?.scrollToKpi;
  const isEditMode = !!unitId;
  const isAddingSection = !!parentId; // Adding a section (child) under a department
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();

  // Restructuring the org tree (add/edit/delete units) is governed by
  // `hrcpms.team.manage` — NOT the generic operational `isViewOnly` flag.
  // The /my-organization cards only show the Edit affordance to team
  // managers (superuser | hrcpms.team.manage), so this screen must accept
  // them too. Without this, a Director / Strategy-Team user who holds
  // team.manage is `isViewOnly` and gets silently bounced the instant they
  // tap Edit — i.e. "the edit button does nothing".
  const canManageTeam = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.team.manage')
  );

  useEffect(() => {
    if (isViewOnly && !canManageTeam) {
      if (navigation.canGoBack()) navigation.goBack();
      else navigateToHome();
    }
  }, [isViewOnly, canManageTeam, navigation, navigateToHome]);

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    dropdown: colors.card,
    dropdownBorder: colors.border,
    dropdownText: isDark ? '#6B7280' : '#9CA3AF',
    dropdownTextSelected: colors.textPrimary,
    dropdownMenu: colors.card,
    dropdownMenuBorder: colors.border,
    dropdownItem: colors.textPrimary,
    dropdownItemBorder: isDark ? colors.border : '#F3F4F6',
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiHeader: colors.card,
    kpiHeaderTitle: colors.textPrimary,
    kpiNumberBadge: isDark ? colors.backgroundAlt : '#E2E8F0',
    kpiSubsection: isDark ? colors.backgroundAlt : '#F1F5F9',
    weightInputWrapper: colors.card,
    weightButton: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
    weightButtonBorder: isDark ? '#4B5563' : '#CBD5E1',
    addKpiButton: colors.card,
    addKpiButtonBorder: isDark ? '#4B5563' : '#CBD5E1',
    addKpiButtonText: colors.textPrimary,
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    loadingText: colors.textMuted,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };

  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionRef = useRef<View>(null);
  const [kpiSectionY, setKpiSectionY] = useState(0);

  const { width } = useWindowDimensions();
  const { showSuccess, showError } = useNotification();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [goals, setGoals] = useState<StrategicGoalList[]>([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    description: '',
    strategicGoals: [] as number[],
  });

  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ id: string; backendId: number | null; name: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [kpiSectionExpanded, setKpiSectionExpanded] = useState(true);

  useUnsavedChangesWarning(!!formData.name || !!formData.name_ar || !!formData.description);

  // Dropdowns
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);

  // KPI Dropdown state: { kpiId, dropdownType }
  const [activeKpiDropdown, setActiveKpiDropdown] = useState<{ kpiId: string; type: string } | null>(null);

  const isSmallScreen = width < 768;

  // Dropdown options for KPIs
  const kpiTypeOptions = [
    // 'strategic' omitted — it requires data_source_type (مستقل/تجميعي, DTS
    // 4.3), collected only on AddGoalScreen. Offering it on an operational
    // screen 400s the KPI create and silently drops it on save.
    { value: 'operational', label: t('kpis.operational') },
  ];

  const polarityOptions = [
    { value: 'positive', label: t('kpis.positiveAscending') },
    { value: 'negative', label: t('kpis.negativeDescending') },
  ];

  const unitOptions = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'عدد', label: t('kpis.number') },
    { value: 'درهم', label: t('kpis.currency') },
    { value: 'يوم', label: t('kpis.day') },
  ];

  const frequencyOptions = [
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  // Toggle KPI dropdown
  const toggleKpiDropdown = (kpiId: string, dropdownType: string) => {
    if (activeKpiDropdown?.kpiId === kpiId && activeKpiDropdown?.type === dropdownType) {
      setActiveKpiDropdown(null);
    } else {
      setActiveKpiDropdown({ kpiId, type: dropdownType });
    }
    // Close other dropdowns
    setShowGoalDropdown(false);
  };

  useEffect(() => {
    fetchData();
  }, [unitId]);

  // Scroll to KPI section if requested
  useEffect(() => {
    if (scrollToKpi && !loading && kpiSectionY > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: kpiSectionY - 100, animated: true });
      }, 300);
    }
  }, [scrollToKpi, loading, kpiSectionY]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // For child units, only show the parent's strategic goals (cascading)
      // For top-level units, show all org goals
      let goalsResults: StrategicGoalList[] = [];
      if (parentId) {
        // Fetch parent unit to get its strategic goals
        const parentUnit = await organizationalUnitsApi.get(parentId);
        const parentGoalIds: number[] = parentUnit.strategic_goal_ids || [];
        if (parentGoalIds.length > 0) {
          const goalsData = await goalsApi.list({ page_size: 100 });
          goalsResults = (goalsData.results || []).filter(
            (g: StrategicGoalList) => parentGoalIds.includes(g.id)
          );
        }
      } else {
        const goalsData = await goalsApi.list({ page_size: 100 });
        goalsResults = goalsData.results || [];
      }
      setGoals(goalsResults);

      // If editing, fetch the organizational unit data and its KPIs
      if (unitId) {
        const unitData = await organizationalUnitsApi.get(unitId);
        setFormData({
          name: unitData.name || '',
          name_ar: unitData.name_ar || '',
          description: unitData.description || '',
          strategicGoals: unitData.strategic_goal_ids || [],
        });

        // Fetch existing KPIs linked to this organizational unit
        try {
          const kpisData = await kpisApi.list({ organizational_unit: unitId, page_size: 100 });
          const existingKpis = (kpisData.results || []).map((kpi: any) => ({
            id: String(kpi.id),
            name: kpi.name || '',
            description: kpi.description || '',
            weight: kpi.weight ? String(kpi.weight) : '',
            kpiType: kpi.kpi_type || '',
            polarity: kpi.polarity || '',
            unit: kpi.unit || '',
            targetValue: kpi.target_value ? String(kpi.target_value) : '',
            worstAcceptableValue: kpi.worst_acceptable_value != null ? String(kpi.worst_acceptable_value) : '',
            frequency: kpi.frequency || '',
            measurementFormula: kpi.measurement_formula || '',
            dataSource: kpi.data_source || '',
            measurementStartDate: kpi.measurement_start_date || '',
            isExpanded: false,
          }));
          if (existingKpis.length > 0) {
            setKpis(existingKpis);
          }
        } catch (kpiErr) {
          console.warn('Failed to load KPIs for unit:', kpiErr);
        }
      }
    } catch (err) {
      showError(t('common.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('organizationalUnits.departmentNameRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showError(t('validation.fillRequired'));
      return;
    }

    setSubmitting(true);
    try {
      // Get user's organization
      const organizationId = user?.profile?.organization;
      if (!organizationId) {
        showError(t('messages.error.noOrganizationAssigned'));
        return;
      }

      // Prepare organizational unit data
      // unit_type: 'department' if no parent (top-level), 'section' if has parent
      const unitData: Partial<OrganizationalUnit> = {
        department_id: organizationId,
        name: formData.name,
        name_ar: formData.name_ar || formData.name,
        description: formData.description || '',
        unit_type: parentId ? 'section' : 'department',
        parent: parentId || null, // parentId for sections, null for top-level departments
      };

      let savedUnitId: number;

      if (isEditMode && unitId) {
        // Update existing unit
        await organizationalUnitsApi.update(unitId, unitData);
        savedUnitId = unitId;
      } else {
        // Create new unit
        const createdUnit = await organizationalUnitsApi.create(unitData);
        savedUnitId = createdUnit.id;
      }

      // Link strategic goals via M2M (strategic_goal_ids on the unit)
      if (formData.strategicGoals.length > 0 || isEditMode) {
        await organizationalUnitsApi.update(savedUnitId, {
          strategic_goal_ids: formData.strategicGoals,
        } as any);
      }

      // Handle KPIs - Note: KPIs for organizational units are typically linked via unit goals
      // This implementation creates operational KPIs linked to the organization
      if (kpis.length > 0) {
        const kpiPromises = kpis
          .filter(kpi => kpi.name.trim())
          .map(async kpi => {
            const kpiData: Record<string, any> = {
              name: kpi.name,
              description: kpi.description,
              kpi_type: (kpi.kpiType || 'operational') as KPIType,
              polarity: (kpi.polarity || 'positive') as Polarity,
              unit: kpi.unit || 'عدد',
              target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
              worst_acceptable_value:
                kpi.polarity === 'negative' && kpi.unit !== '%' && kpi.worstAcceptableValue !== ''
                  ? (parseFloat(kpi.worstAcceptableValue) || 0).toFixed(2)
                  : null,
              frequency: (kpi.frequency || 'monthly') as Frequency,
              measurement_formula: kpi.measurementFormula,
              data_source: kpi.dataSource,
              measurement_start_date: kpi.measurementStartDate || null,
              weight: kpi.weight || '0',
              source_type: 'strategic_goal',
              strategic_goal: formData.strategicGoals.length > 0 ? formData.strategicGoals[0] : null,
              organizational_unit: savedUnitId,
            };

            // Check if this is an existing KPI (numeric ID) or new one (random string ID)
            const numericId = parseInt(kpi.id, 10);
            if (!isNaN(numericId) && numericId > 0) {
              try {
                return await kpisApi.update(numericId, kpiData);
              } catch (err: any) {
                if (err.response?.status === 404) {
                  return kpisApi.create(kpiData);
                }
                throw err;
              }
            } else {
              return kpisApi.create(kpiData);
            }
          });

        await Promise.all(kpiPromises);
      }

      const successMessage = isEditMode
        ? (isAddingSection ? t('organizationalUnits.sectionUpdated') : t('organizationalUnits.departmentUpdated'))
        : (isAddingSection ? t('organizationalUnits.sectionCreated') : t('organizationalUnits.departmentCreated'));
      showSuccess(successMessage);
      invalidateData('my-organization');
      navigation.navigate('MyOrganization' as never, { scrollToUnits: true } as never);
    } catch (err: any) {
      console.error('Error saving organizational unit:', err);

      let errorMessage = isEditMode ? t('organizationalUnits.updateFailed') : t('organizationalUnits.createFailed');
      if (err.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors) {
          errorMessage = data.non_field_errors[0];
        } else {
          const fieldErrors = Object.entries(data)
            .filter(([key, value]) => Array.isArray(value))
            .map(([key, value]) => `${key}: ${(value as string[]).join(', ')}`)
            .join('\n');
          if (fieldErrors) {
            errorMessage = fieldErrors;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const addKPI = () => {
    setKpis([...kpis, createEmptyKPI()]);
  };

  const removeKPI = (id: string) => {
    const target = kpis.find(k => k.id === id);
    const numericId = parseInt(id, 10);
    const backendId = !isNaN(numericId) && numericId > 0 ? numericId : null;
    setKpiPendingDelete({
      id,
      backendId,
      name: (target as any)?.name || '',
    });
  };

  const confirmRemoveKPI = async () => {
    if (!kpiPendingDelete) return;
    const { id, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      try {
        await kpisApi.delete(backendId);
        showSuccess(t('kpis.deleteSuccess'));
      } catch (err) {
        showError(t('kpis.deleteFailed'));
        setKpiPendingDelete(null);
        return;
      }
    }
    setKpis(prev => prev.filter(kpi => kpi.id !== id));
    setKpiPendingDelete(null);
  };

  const updateKPI = (id: string, field: keyof KPIFormData, value: any) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, [field]: value } : kpi));
  };

  const toggleKPIExpanded = (id: string) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi));
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={styles.loadingText}>{t('common.loadingData')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, isSmallScreen && styles.containerSmall]}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={
              isAddingSection && parentName
                ? [
                    { label: t('navigation.home') },
                    { label: t('navigation.myOrganization'), route: 'MyOrganization' },
                    { label: parentName },
                    { label: isEditMode ? t('organizationalUnits.editChildUnit') : t('organizationalUnits.addChildUnit') },
                  ]
                : [
                    { label: t('navigation.home') },
                    { label: t('navigation.myOrganization'), route: 'MyOrganization' },
                    { label: isEditMode ? t('organizationalUnits.editUnit') : t('organizationalUnits.addUnit') },
                  ]
            }
          />

          {/* Main Card Container */}
          <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
            {/* Title */}
            <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>
              {isEditMode ? (isAddingSection ? t('organizationalUnits.editChildUnit') : t('organizationalUnits.editUnit')) : (isAddingSection ? t('organizationalUnits.addChildUnit') : t('organizationalUnits.addUnit'))}
            </Text>

            {/* Basic Information Section */}
            <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: showGoalDropdown ? 9999 : 20, overflow: 'visible' }]}>
              <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>

              <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: showGoalDropdown ? 1 : 10, overflow: 'visible' }]}>
                {/* Department/Section Name - RIGHT side in RTL */}
                <View style={[styles.formGroup, styles.formGroupHalf]}>
                  <Text style={styles.label}>
                    {isAddingSection ? t('organizationalUnits.childUnitName') : t('organizationalUnits.unitName')} <Text style={styles.required}>*</Text>
                  </Text>
                  <TextInput
                    style={[styles.input, errors.name ? styles.inputError : undefined]}
                    // Display the name matching the active UI language. In edit mode
                    // both name (EN) and name_ar (AR) are loaded; without this the
                    // Arabic UI would show the English name. Fall back to the other
                    // language when one side is empty.
                    value={language === 'ar' ? (formData.name_ar || formData.name) : (formData.name || formData.name_ar)}
                    onChangeText={(text) => setFormData({ ...formData, name: text, name_ar: text })}
                    placeholder=""
                    placeholderTextColor="#9CA3AF"
                    textAlign="right"
                  />
                  {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
                </View>

                {/* LEFT side in RTL: Parent unit (read-only) for child units */}
                {isAddingSection && (
                  <View style={[styles.formGroup, styles.formGroupHalf]}>
                    <Text style={styles.label}>
                      {t('organizationalUnits.parentUnit')} <Text style={styles.required}>*</Text>
                    </Text>
                    <View style={[styles.dropdown, { backgroundColor: isDark ? colors.backgroundAlt : '#F9FAFB', opacity: 0.5 }]}>
                      <ChevronDownIcon size={16} color="#6B7280" />
                      <Text style={[styles.dropdownText, { color: colors.textPrimary }]}>
                        {parentName || t('organizationalUnits.parentUnit')}
                      </Text>
                    </View>
                  </View>
                )}
              </View>

              {/* Strategic Goals - full width row (for both top-level and child units) */}
              <View style={[styles.formGroup, { zIndex: showGoalDropdown ? 9999 : 1, overflow: 'visible' }]}>
                <Text style={styles.label}>
                  {t('goals.strategicGoals')} <Text style={styles.required}>*</Text>
                </Text>
                <View style={[styles.dropdownWrapper, { zIndex: showGoalDropdown ? 9999 : 1 }]}>
                  <TouchableOpacity
                    style={[styles.dropdown, { minHeight: 'auto' as any, flexWrap: 'wrap' }]}
                    onPress={() => {
                      setShowGoalDropdown(!showGoalDropdown);
                      setActiveKpiDropdown(null);
                    }}
                  >
                    <ChevronDownIcon size={16} color="#6B7280" />
                    {formData.strategicGoals.length > 0 ? (
                      <View style={{ flex: 1, flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 8 }}>
                        {formData.strategicGoals.map(gid => {
                          const goal = goals.find(g => g.id === gid);
                          return (
                            <View key={gid} style={styles.selectedGoalChip}>
                              <TouchableOpacity
                                onPress={(e) => {
                                  e.stopPropagation();
                                  setFormData({ ...formData, strategicGoals: formData.strategicGoals.filter(id => id !== gid) });
                                }}
                                style={styles.chipRemoveButton}
                              >
                                <Text style={styles.chipRemoveText}>×</Text>
                              </TouchableOpacity>
                              <Text style={styles.selectedGoalChipText} numberOfLines={1}>
                                {goal?.title || `#${gid}`}
                              </Text>
                            </View>
                          );
                        })}
                      </View>
                    ) : (
                      <Text style={styles.dropdownText}>
                        {t('organizationalUnits.selectStrategicGoal')}
                      </Text>
                    )}
                  </TouchableOpacity>
                  {showGoalDropdown && (
                    <View style={styles.dropdownMenu}>
                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                        {goals.map(goal => {
                          const isSelected = formData.strategicGoals.includes(goal.id);
                          return (
                            <TouchableOpacity
                              key={goal.id}
                              style={[styles.dropdownItem, isSelected && styles.dropdownItemSelected]}
                              onPress={() => {
                                if (isSelected) {
                                  setFormData({ ...formData, strategicGoals: formData.strategicGoals.filter(id => id !== goal.id) });
                                } else {
                                  setFormData({ ...formData, strategicGoals: [...formData.strategicGoals, goal.id] });
                                }
                              }}
                            >
                              <View style={[styles.checkbox, isSelected && styles.checkboxChecked]}>
                                {isSelected && <Text style={styles.checkboxCheck}>✓</Text>}
                              </View>
                              <Text style={[styles.dropdownItemText, isSelected && { fontFamily: FONTS.medium }]}>{goal.title}</Text>
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    </View>
                  )}
                </View>
              </View>

              {/* Description */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>{isAddingSection ? t('organizationalUnits.childUnitDescription') : t('organizationalUnits.unitDescription')} <Text style={styles.required}>*</Text></Text>
                <TextInput
                  style={[styles.input, styles.textarea]}
                  value={formData.description}
                  onChangeText={(text) => setFormData({ ...formData, description: text })}
                  placeholder=""
                  placeholderTextColor="#9CA3AF"
                  multiline
                  numberOfLines={3}
                  textAlign="right"
                  textAlignVertical="top"
                />
              </View>
            </View>

            {/* KPIs Section - Collapsible */}
            <View
              style={[styles.section, { zIndex: activeKpiDropdown ? 9999 : 10, overflow: 'visible' }]}
              onLayout={(event) => {
                const { y } = event.nativeEvent.layout;
                setKpiSectionY(y);
              }}
            >
              <TouchableOpacity
                style={styles.sectionHeader}
                onPress={() => setKpiSectionExpanded(!kpiSectionExpanded)}
              >
                {kpiSectionExpanded ? (
                  <ChevronUpIcon size={20} color="#6B7280" />
                ) : (
                  <ChevronDownIcon size={20} color="#6B7280" />
                )}
                <Text style={styles.sectionTitle}>{t('organizationalUnits.kpiIndicators')}</Text>
              </TouchableOpacity>

              {kpiSectionExpanded && (
                <>
                  {kpis.map((kpi, index) => (
                    <View key={kpi.id} style={[styles.kpiCard, activeKpiDropdown?.kpiId === kpi.id && { zIndex: 100 }]}>
                      {/* KPI Header */}
                      <View style={styles.kpiHeader}>
                        {/* RIGHT side: Drag handle */}
                        <MenuIcon size={20} color="#6B7280" />
                        {/* Title */}
                        <Text style={styles.kpiHeaderTitle}>{kpi.name || t('organizationalUnits.secondKpiName')}</Text>
                        {/* Number badge */}
                        <View style={styles.kpiNumberBadge}>
                          <Text style={styles.kpiNumberText}>{index + 1}</Text>
                        </View>
                        {/* Trash icon */}
                        <TouchableOpacity onPress={() => removeKPI(kpi.id)} style={styles.deleteButton}>
                          <TrashIcon size={18} color="#6B7280" />
                        </TouchableOpacity>
                        {/* LEFT side: Expand/Collapse */}
                        <TouchableOpacity onPress={() => toggleKPIExpanded(kpi.id)}>
                          {kpi.isExpanded ? (
                            <ChevronUpIcon size={20} color="#6B7280" />
                          ) : (
                            <ChevronDownIcon size={20} color="#6B7280" />
                          )}
                        </TouchableOpacity>
                      </View>

                      {kpi.isExpanded && (
                        <View style={[styles.kpiContent, activeKpiDropdown?.kpiId === kpi.id && { zIndex: 100, overflow: 'visible' }]}>
                          {/* Basic Info Subsection */}
                          <View style={styles.kpiSubsection}>
                            <Text style={styles.kpiSectionTitle}>{t('common.basicInfo')}</Text>
                            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                              {/* KPI Name - RIGHT side in RTL */}
                              <View style={[styles.formGroup, styles.formGroupHalf]}>
                                <Text style={styles.label}>{t('kpis.kpiName')} <Text style={styles.required}>*</Text></Text>
                                <TextInput
                                  style={styles.input}
                                  value={kpi.name}
                                  onChangeText={(text) => updateKPI(kpi.id, 'name', text)}
                                  placeholder=""
                                  placeholderTextColor="#9CA3AF"
                                  textAlign="right"
                                />
                              </View>
                              {/* Weight - LEFT side in RTL */}
                              <View style={[styles.formGroup, styles.formGroupHalf]}>
                                <Text style={styles.label}>{t('kpis.weight')} <Text style={styles.required}>*</Text></Text>
                                <View style={styles.weightInputRow}>
                                  <View style={styles.weightInputWrapper}>
                                    <Text style={styles.percentSymbol}>%</Text>
                                    <TextInput
                                      style={styles.weightInput}
                                      value={kpi.weight}
                                      onChangeText={(text) => updateKPI(kpi.id, 'weight', normalizeArabicNumbers(text))}
                                      placeholder="30"
                                      placeholderTextColor="#9CA3AF"
                                      keyboardType="numeric"
                                      textAlign="right"
                                    />
                                  </View>
                                  <TouchableOpacity style={styles.weightButton}>
                                    <Text style={styles.weightButtonText}>{t('common.assignWeight')}</Text>
                                  </TouchableOpacity>
                                </View>
                              </View>
                            </View>

                            <View style={styles.formGroup}>
                              <Text style={styles.label}>{t('kpis.description')} <Text style={styles.required}>*</Text></Text>
                              <TextInput
                                style={[styles.input, styles.textarea]}
                                value={kpi.description}
                                onChangeText={(text) => updateKPI(kpi.id, 'description', text)}
                                placeholder=""
                                placeholderTextColor="#9CA3AF"
                                multiline
                                numberOfLines={2}
                                textAlign="right"
                                textAlignVertical="top"
                              />
                            </View>
                          </View>

                          {/* Classification Subsection */}
                          <View style={[styles.kpiSubsection, { zIndex: 30, overflow: 'visible' }]}>
                            <Text style={styles.kpiSectionTitle}>{t('kpis.classification')}</Text>
                            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: 10, overflow: 'visible' }]}>
                              {/* KPI Type - RIGHT side in RTL */}
                              <View style={[styles.formGroup, styles.formGroupHalf, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' && { zIndex: 100 }]}>
                                <Text style={styles.label}>{t('kpis.kpiClassification')} <Text style={styles.required}>*</Text></Text>
                                <View style={[styles.dropdownWrapper, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' && { zIndex: 100 }]}>
                                  <TouchableOpacity
                                    style={styles.dropdown}
                                    onPress={() => toggleKpiDropdown(kpi.id, 'kpiType')}
                                  >
                                    <ChevronDownIcon size={16} color="#6B7280" />
                                    <Text style={[styles.dropdownText, kpi.kpiType ? styles.dropdownTextSelected : undefined]}>
                                      {kpiTypeOptions.find(o => o.value === kpi.kpiType)?.label || t('kpis.selectClassification')}
                                    </Text>
                                  </TouchableOpacity>
                                  {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'kpiType' && (
                                    <View style={styles.dropdownMenu}>
                                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                        {kpiTypeOptions.map(option => (
                                          <TouchableOpacity
                                            key={option.value}
                                            style={styles.dropdownItem}
                                            onPress={() => {
                                              updateKPI(kpi.id, 'kpiType', option.value);
                                              setActiveKpiDropdown(null);
                                            }}
                                          >
                                            <Text style={styles.dropdownItemText}>{option.label}</Text>
                                          </TouchableOpacity>
                                        ))}
                                      </ScrollView>
                                    </View>
                                  )}
                                </View>
                              </View>
                              {/* Polarity - LEFT side in RTL */}
                              <View style={[styles.formGroup, styles.formGroupHalf, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' && { zIndex: 100 }]}>
                                <Text style={styles.label}>{t('kpis.polarity')} <Text style={styles.required}>*</Text></Text>
                                <View style={[styles.dropdownWrapper, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' && { zIndex: 100 }]}>
                                  <TouchableOpacity
                                    style={styles.dropdown}
                                    onPress={() => toggleKpiDropdown(kpi.id, 'polarity')}
                                  >
                                    <ChevronDownIcon size={16} color="#6B7280" />
                                    <Text style={[styles.dropdownText, kpi.polarity ? styles.dropdownTextSelected : undefined]}>
                                      {polarityOptions.find(o => o.value === kpi.polarity)?.label || t('kpis.selectPolarityHint')}
                                    </Text>
                                  </TouchableOpacity>
                                  {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'polarity' && (
                                    <View style={styles.dropdownMenu}>
                                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                        {polarityOptions.map(option => (
                                          <TouchableOpacity
                                            key={option.value}
                                            style={styles.dropdownItem}
                                            onPress={() => {
                                              updateKPI(kpi.id, 'polarity', option.value);
                                              setActiveKpiDropdown(null);
                                            }}
                                          >
                                            <Text style={styles.dropdownItemText}>{option.label}</Text>
                                          </TouchableOpacity>
                                        ))}
                                      </ScrollView>
                                    </View>
                                  )}
                                </View>
                              </View>
                            </View>
                          </View>

                          {/* Measurement Subsection */}
                          <View style={[styles.kpiSubsection, { zIndex: 20, overflow: 'visible' }]}>
                            <Text style={styles.kpiSectionTitle}>{t('kpis.measurement')}</Text>

                            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                              {/* Start Date - LEFT */}
                              <View style={[styles.formGroup, styles.formGroupHalf]}>
                                <Text style={styles.label}>{t('kpis.measurementStartDate')} <Text style={styles.required}>*</Text></Text>
                                <View style={styles.dateInputWrapper}>
                                  <input
                                    type="date"
                                    value={kpi.measurementStartDate}
                                    onChange={(e: any) => updateKPI(kpi.id, 'measurementStartDate', e.target.value)}
                                    style={{
                                      flex: 1,
                                      border: 'none',
                                      outline: 'none',
                                      backgroundColor: 'transparent',
                                      fontSize: 14,
                                      color: kpi.measurementStartDate ? '#414042' : '#9CA3AF',
                                      textAlign: 'right' as const,
                                      direction: 'rtl' as const,
                                      padding: '10px 0',
                                      cursor: 'pointer',
                                    }}
                                    placeholder="يوم/شهر/سنة"
                                  />
                                </View>
                              </View>
                            </View>

                            {/* Unit and Frequency Row */}
                            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall, { zIndex: 10, overflow: 'visible' }]}>
                              {/* Unit - RIGHT side in RTL */}
                              <View style={[styles.formGroup, styles.formGroupHalf, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'unit' && { zIndex: 100 }]}>
                                <Text style={styles.label}>{t('kpis.measurementUnit')} <Text style={styles.required}>*</Text></Text>
                                <View style={[styles.dropdownWrapper, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'unit' && { zIndex: 100 }]}>
                                  <TouchableOpacity
                                    style={styles.dropdown}
                                    onPress={() => toggleKpiDropdown(kpi.id, 'unit')}
                                  >
                                    <ChevronDownIcon size={16} color="#6B7280" />
                                    <Text style={[styles.dropdownText, kpi.unit ? styles.dropdownTextSelected : undefined]}>
                                      {unitOptions.find(o => o.value === kpi.unit)?.label || t('kpis.selectUnitHint')}
                                    </Text>
                                  </TouchableOpacity>
                                  {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'unit' && (
                                    <View style={styles.dropdownMenu}>
                                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                        {unitOptions.map(option => (
                                          <TouchableOpacity
                                            key={option.value}
                                            style={styles.dropdownItem}
                                            onPress={() => {
                                              updateKPI(kpi.id, 'unit', option.value);
                                              setActiveKpiDropdown(null);
                                            }}
                                          >
                                            <Text style={styles.dropdownItemText}>{option.label}</Text>
                                          </TouchableOpacity>
                                        ))}
                                      </ScrollView>
                                    </View>
                                  )}
                                </View>
                              </View>
                              {/* Frequency - LEFT side in RTL */}
                              <View style={[styles.formGroup, styles.formGroupHalf, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'frequency' && { zIndex: 100 }]}>
                                <Text style={styles.label}>{t('kpis.measurementFrequency')} <Text style={styles.required}>*</Text></Text>
                                <View style={[styles.dropdownWrapper, activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'frequency' && { zIndex: 100 }]}>
                                  <TouchableOpacity
                                    style={styles.dropdown}
                                    onPress={() => toggleKpiDropdown(kpi.id, 'frequency')}
                                  >
                                    <ChevronDownIcon size={16} color="#6B7280" />
                                    <Text style={[styles.dropdownText, kpi.frequency ? styles.dropdownTextSelected : undefined]}>
                                      {frequencyOptions.find(o => o.value === kpi.frequency)?.label || t('kpis.selectFrequency')}
                                    </Text>
                                  </TouchableOpacity>
                                  {activeKpiDropdown?.kpiId === kpi.id && activeKpiDropdown?.type === 'frequency' && (
                                    <View style={styles.dropdownMenu}>
                                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
                                        {frequencyOptions.map(option => (
                                          <TouchableOpacity
                                            key={option.value}
                                            style={styles.dropdownItem}
                                            onPress={() => {
                                              updateKPI(kpi.id, 'frequency', option.value);
                                              setActiveKpiDropdown(null);
                                            }}
                                          >
                                            <Text style={styles.dropdownItemText}>{option.label}</Text>
                                          </TouchableOpacity>
                                        ))}
                                      </ScrollView>
                                    </View>
                                  )}
                                </View>
                              </View>
                            </View>

                            <View style={styles.formGroup}>
                              <Text style={styles.label}>{t('kpis.measurementFormula')} <Text style={styles.required}>*</Text></Text>
                              <TextInput
                                style={[styles.input, styles.textarea]}
                                value={kpi.measurementFormula}
                                onChangeText={(text) => updateKPI(kpi.id, 'measurementFormula', text)}
                                placeholder={t('kpis.formulaPlaceholder')}
                                placeholderTextColor="#9CA3AF"
                                multiline
                                numberOfLines={2}
                                textAlign="right"
                                textAlignVertical="top"
                              />
                            </View>

                            <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                              {/* Target Value - RIGHT side in RTL */}
                              <View style={[styles.formGroup, styles.formGroupHalf]}>
                                <Text style={styles.label}>{t('kpis.targetValue')} <Text style={styles.required}>*</Text></Text>
                                <TextInput
                                  style={styles.input}
                                  value={kpi.targetValue}
                                  onChangeText={(text) => updateKPI(kpi.id, 'targetValue', normalizeArabicNumbers(text))}
                                  placeholder="0.0"
                                  placeholderTextColor="#9CA3AF"
                                  keyboardType="numeric"
                                  textAlign="right"
                                />
                              </View>
                              {/* Data Source - LEFT side in RTL */}
                              <View style={[styles.formGroup, styles.formGroupHalf]}>
                                <Text style={styles.label}>{t('kpis.dataSource')} <Text style={styles.required}>*</Text></Text>
                                <TextInput
                                  style={styles.input}
                                  value={kpi.dataSource}
                                  onChangeText={(text) => updateKPI(kpi.id, 'dataSource', text)}
                                  placeholder={t('kpis.dataSourcePlaceholder')}
                                  placeholderTextColor="#9CA3AF"
                                  textAlign="right"
                                />
                              </View>
                            </View>

                            {/* Worst Acceptable Value — only for negative polarity + non-% unit */}
                            {kpi.polarity === 'negative' && kpi.unit && kpi.unit !== '%' && (
                              <View style={[styles.formRow, isSmallScreen && styles.formRowSmall]}>
                                <View style={[styles.formGroup, styles.formGroupHalf]}>
                                  <Text style={styles.label}>
                                    {t('kpis.worstAcceptableValue')}
                                    {parseFloat(kpi.targetValue) === 0 && (
                                      <Text style={styles.required}> *</Text>
                                    )}
                                  </Text>
                                  <TextInput
                                    style={styles.input}
                                    value={kpi.worstAcceptableValue}
                                    onChangeText={(text) => updateKPI(kpi.id, 'worstAcceptableValue', normalizeArabicNumbers(text))}
                                    placeholder={t('kpis.worstAcceptableValueHint')}
                                    placeholderTextColor="#9CA3AF"
                                    keyboardType="numeric"
                                    textAlign="right"
                                  />
                                </View>
                              </View>
                            )}
                          </View>
                        </View>
                      )}
                    </View>
                  ))}

                  {/* Add KPI Button */}
                  <TouchableOpacity style={styles.addKpiButton} onPress={addKPI}>
                    <PlusIcon size={16} color="#6B7280" />
                    <Text style={styles.addKpiButtonText}>{t('organizationalUnits.addKpi')}</Text>
                  </TouchableOpacity>
                </>
              )}
            </View>

            {/* Footer Buttons */}
            <View style={styles.footer}>
              <TouchableOpacity style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]} onPress={handleCancel}>
                <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveButton, submitting && styles.saveButtonDisabled]}
                onPress={handleSave}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.saveButtonText}>{isEditMode ? t('common.update') : t('common.save')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmRemoveKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: 24,
    paddingTop: 16,
    overflow: 'visible',
  },
  containerSmall: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  breadcrumbItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  breadcrumbText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  breadcrumbActive: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },

  // Title
  pageTitle: {
    fontSize: 24,
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: 'right',
    marginBottom: 24,
  },

  // Section
  section: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    padding: 24,
    gap: 24,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'visible',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: 'right',
  },

  // Form
  formRow: {
    flexDirection: 'row-reverse',
    gap: 24,
    overflow: 'visible',
  },
  formRowSmall: {
    flexDirection: 'column',
  },
  formGroup: {
    overflow: 'visible',
  },
  formGroupHalf: {
    flex: 1,
    overflow: 'visible',
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.neutral700,
    textAlign: 'right',
    marginBottom: 8,
  },
  required: {
    color: COLORS.primary,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  inputError: {
    borderColor: '#EF4444',
  },
  textarea: {
    minHeight: 60,
    textAlignVertical: 'top',
  },
  errorText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },

  // Dropdown
  dropdownWrapper: {
    position: 'relative',
    zIndex: 10,
    overflow: 'visible',
  },
  dropdown: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dropdownText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    flex: 1,
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  dropdownTextSelected: {
    color: COLORS.neutral900,
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    left: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    marginTop: 4,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
    elevation: 10,
    zIndex: 9999,
    maxHeight: 200,
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
  },
  dropdownItemSelected: {
    backgroundColor: '#F0F9FF',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral700,
    textAlign: 'right',
    writingDirection: 'rtl',
    flex: 1,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#CBD5E1',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkboxChecked: {
    backgroundColor: COLORS.primary,
    borderColor: COLORS.primary,
  },
  checkboxCheck: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 14,
  },
  selectedGoalChip: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#9467BD',
    borderRadius: 4,
    paddingHorizontal: 4,
    paddingVertical: 2,
    gap: 4,
    maxWidth: '100%',
  },
  selectedGoalChipText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    flexShrink: 1,
  },
  chipRemoveButton: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chipRemoveText: {
    fontSize: 14,
    color: '#9467BD',
    fontWeight: '400',
    lineHeight: 16,
  },

  // Weight Input
  weightInputRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
  },
  weightInputWrapper: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  percentSymbol: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginLeft: 8,
  },
  weightInput: {
    flex: 1,
    paddingVertical: 12,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.neutral900,
    textAlign: 'right',
  },
  weightButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  weightButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.neutral700,
  },

  // Date Input
  dateInputWrapper: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },

  // KPI Card
  kpiCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    marginBottom: 16,
    gap: 24,
    overflow: 'visible',
  },
  kpiHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#FFFFFF',
    gap: 12,
  },
  kpiHeaderTitle: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  kpiNumberBadge: {
    width: 40,
    height: 40,
    minWidth: 40,
    minHeight: 40,
    borderRadius: 4,
    backgroundColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  kpiNumberText: {
    fontSize: 20,
    fontFamily: FONTS.medium,
    fontWeight: '700',
    color: '#414042',
    lineHeight: 26,
  },
  deleteButton: {
    padding: 4,
  },
  kpiContent: {
    padding: 24,
    gap: 24,
    overflow: 'visible',
  },
  kpiSubsection: {
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    padding: 24,
    gap: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    overflow: 'visible',
  },
  kpiSectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: 'right',
  },

  // Add KPI Button
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    gap: 8,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  addKpiButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
  },

  // Footer
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
    marginBottom: 24,
  },
  saveButton: {
    backgroundColor: COLORS.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 80,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});
